import { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Sparkles, Crown } from "lucide-react";
import { useEntitlements } from "@/lib/useEntitlements";

/** Header pill: trial days left / founding spot / plan lapsed. */
export const PlanPill = () => {
  const { ent } = useEntitlements();
  if (!ent) return null;
  let text = null, cls = "border-amber-400/60 text-amber-300 md:text-amber-700 md:border-amber-400";
  if (ent.status === "trial") text = `${ent.trial_days_left} day${ent.trial_days_left === 1 ? "" : "s"} left · Pick a plan`;
  else if (ent.status === "lapsed") { text = "Plan ended · Pick a plan"; cls = "border-rose-400/70 text-rose-300 md:text-rose-700 md:border-rose-400"; }
  else if (ent.status === "past_due") { text = "Card declined · Fix billing"; cls = "border-rose-400/70 text-rose-300 md:text-rose-700 md:border-rose-400"; }
  else if (ent.status === "founding" && ent.founding) text = `Founding #${ent.founding_number} · ${ent.founding.taken}/${ent.founding.cap}`;
  else if (ent.status === "founding_free") text = `Founding #${ent.founding_number} · free`;
  if (!text) return null;
  return (
    <Link to="/settings?tab=plan" data-testid="plan-pill"
      className={`hidden xs:inline-flex sm:inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${cls}`}>
      {ent.status === "lapsed" || ent.status === "past_due" ? <Lock className="w-3 h-3" /> : <Crown className="w-3 h-3" />}{text}
    </Link>
  );
};

/** Read-only banner when a plan has lapsed + toast on 402s. */
export const PlanLockBanner = () => {
  const { ent, refetch } = useEntitlements();
  useEffect(() => {
    const on = (e) => {
      const d = e.detail || {};
      toast.error(d.detail || "This needs a bigger plan", {
        action: { label: d.code === "plan_lapsed" ? "Pick a plan" : "See plans", onClick: () => { window.location.href = "/settings?tab=plan"; } },
      });
      if (d.code === "plan_lapsed") refetch();
    };
    window.addEventListener("kingly:plan-block", on);
    return () => window.removeEventListener("kingly:plan-block", on);
  }, [refetch]);
  if (!ent?.read_only) return null;
  return (
    <div className="mx-4 sm:mx-6 md:mx-8 mt-4 max-w-7xl md:mx-auto" data-testid="plan-lock-banner">
      <div className="flex items-center gap-3 bg-slate-900 text-white rounded-xl px-4 py-3 shadow-lg border border-rose-400/50">
        <Lock className="w-4 h-4 text-rose-300 shrink-0" />
        <div className="text-sm flex-1"><b>Your Kingly plan has ended.</b> Everything is still here to view; owners' pay links keep working. Pick a plan to make changes again.</div>
        <Link to="/settings?tab=plan" className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-400 text-slate-900 font-semibold px-4 py-1.5 text-xs" data-testid="plan-lock-cta"><Sparkles className="w-3 h-3" /> Pick a plan</Link>
      </div>
    </div>
  );
};
